import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { MangoUser, SubscriptionTier } from '../types';
import { API_BASE_URL } from '../services/api';
import { getTokenFromHash, clearHash } from 'shared-ui';

/**
 * Mirrors the session token into native SharedPreferences (via @capacitor/preferences) when
 * running inside the Android wrapper (mobile-apps/mango-personal) — a plain WebView's
 * localStorage isn't readable from native Kotlin code, but SharedPreferences is, which is how
 * the home-screen widget's background sync worker authenticates without the app being open.
 * No-op on web (Capacitor.isNativePlatform() is false there).
 */
function setStoredToken(token: string) {
  localStorage.setItem('mangoPersonalToken', token);
  if (Capacitor.isNativePlatform()) {
    Preferences.set({ key: 'mango_token', value: token }).catch(() => {});
  }
}

function clearStoredToken() {
  localStorage.removeItem('mangoPersonalToken');
  if (Capacitor.isNativePlatform()) {
    Preferences.remove({ key: 'mango_token' }).catch(() => {});
  }
}

export interface StaffAccount {
  id: string;
  businessId: string;
  role: string;
  business: { id: string; name: string; mangoId?: string };
}

interface AuthContextType {
  user: MangoUser | null;
  staffAccounts: StaffAccount[];
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => void;
  updateUser: (updates: Partial<MangoUser>) => void;
  upgradeSubscription: (tier: SubscriptionTier) => Promise<boolean>;
  refreshStaffAccounts: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Map backend user/account to MangoUser shape */
function mapBackendToMangoUser(raw: any): MangoUser {
  const sub = raw?.tier ?? raw?.subscription ?? 'free';
  const mangoId = raw?.mangoId ?? raw?.personalAccount?.mangoId ?? '';
  const staffProfile = raw?.staffProfile ?? null;
  return {
    id: raw.id,
    mangoId,
    email: raw.email ?? '',
    firstName: raw.firstName ?? '',
    lastName: raw.lastName ?? '',
    phone: raw.phone,
    avatarUrl: raw.avatarUrl,
    subscription: sub === 'premium' ? 'premium' : 'free',
    subscriptionExpiresAt: raw.premiumSince ? new Date(raw.premiumSince).toISOString() : undefined,
    createdAt: raw.createdAt ? new Date(raw.createdAt).toISOString() : new Date().toISOString(),
    wallet: raw.wallet ?? {
      id: `wallet-${raw.id}`,
      address: '',
      mangoCredits: 0,
      pendingCredits: 0,
      weight: 100,
      transactionHistory: [],
    },
    settings: raw.settings ?? {
      notifications: { orders: true, messages: true, promotions: true, priceAlerts: true },
      privacy: { showProfile: true, allowMessages: true, showActivity: false },
      display: { theme: 'system', language: 'en', currency: 'AUD' },
    },
    staffProfile: staffProfile && typeof staffProfile === 'object' ? staffProfile : undefined,
  };
}

/** Production = go-live: no demo fallback, always use live API */
const isProduction = (): boolean => {
  if (typeof window === 'undefined') return true;
  const h = window.location.hostname;
  return h !== 'localhost' && h !== '127.0.0.1';
};

// Generate a unique Mango Personal ID (demo only - localhost)
const generateMangoId = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const nums = '0123456789';
  const prefix = 'MP'; // Mango Personal
  const letters = chars[Math.floor(Math.random() * 26)] + chars[Math.floor(Math.random() * 26)];
  let numbers = '';
  for (let i = 0; i < 6; i++) {
    numbers += nums[Math.floor(Math.random() * 10)];
  }
  return `${prefix}-${letters}-${numbers}`;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<MangoUser | null>(null);
  const [staffAccounts, setStaffAccounts] = useState<StaffAccount[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshStaffAccounts = async () => {
    const token = localStorage.getItem('mangoPersonalToken');
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.staffAccounts?.length) {
          setStaffAccounts(data.staffAccounts);
        }
      }
    } catch {
      setStaffAccounts([]);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      // Cross-app handoff from mango-chat (or any other Mango app): `#mango_token=<jwt>` in the
      // URL. Consume it before the normal localStorage restore so a deep link into this app
      // lands authenticated.
      const hashToken = getTokenFromHash();
      if (hashToken) {
        setStoredToken(hashToken);
        clearHash();
      }

      const token = hashToken || localStorage.getItem('mangoPersonalToken');
      const savedUser = localStorage.getItem('mangoPersonalUser');

      if (token) {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            if (data.type === 'personal' && data.user) {
              setUser(mapBackendToMangoUser(data.user));
              if (data.staffAccounts?.length) setStaffAccounts(data.staffAccounts);
              setLoading(false);
              return;
            }
            if (data.type === 'staff' && data.user) {
              const u = data.user;
              setUser(mapBackendToMangoUser(u));
              setStaffAccounts(u.businessId ? [{
                id: u.id,
                businessId: u.businessId,
                role: u.role ?? 'staff',
                business: { id: u.businessId, name: u.business?.name ?? 'Workplace', mangoId: u.business?.mangoId },
              }] : []);
              setLoading(false);
              return;
            }
          }
          if (!isProduction() && savedUser) setUser(JSON.parse(savedUser));
          else { clearStoredToken(); localStorage.removeItem('mangoPersonalUser'); }
        } catch {
          if (!isProduction() && savedUser) { console.log('Using saved user for demo mode'); setUser(JSON.parse(savedUser)); }
          else { clearStoredToken(); localStorage.removeItem('mangoPersonalUser'); }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, loginContext: 'personal' }),
      });
      if (response.ok) {
        const data = await response.json();
        // Accept both personal and staff logins (staff can access Workplaces)
        const isPersonal = data.type === 'personal';
        const isStaff = data.type === 'staff';
        if (isPersonal || isStaff) {
          const token = data.tokens?.personal ?? data.tokens?.staff ?? data.token;
          const u = data.user ?? data.account;
          if (token && u) {
            const mapped = mapBackendToMangoUser(u);
            setStoredToken(token);
            localStorage.setItem('mangoPersonalUser', JSON.stringify(mapped));
            setUser(mapped);
            if (data.staffAccounts?.length) {
              setStaffAccounts(data.staffAccounts);
            } else if (isStaff && u.businessId) {
              setStaffAccounts([{
                id: u.id,
                businessId: u.businessId,
                role: u.role ?? 'staff',
                business: { id: u.businessId, name: u.business?.name ?? 'Workplace', mangoId: u.business?.mangoId },
              }]);
            }
            return true;
          }
        }
      }
    } catch {
      if (isProduction()) return false;
      console.log('API not available, using demo login');
    }

    if (isProduction()) return false; // No demo fallback on go-live

    // Demo mode (localhost only): create a demo user
    const demoUser: MangoUser = {
      id: 'demo-user-1',
      mangoId: generateMangoId(),
      email: email,
      firstName: email.split('@')[0],
      lastName: 'User',
      subscription: 'free',
      createdAt: new Date().toISOString(),
      wallet: {
        id: 'wallet-1',
        address: '0x' + Array(40).fill(0).map(() => Math.floor(Math.random() * 16).toString(16)).join(''),
        mangoCredits: 150.00,
        pendingCredits: 25.00,
        weight: 1000,
        transactionHistory: [
          {
            id: 'tx-1',
            type: 'earn',
            amount: 50,
            description: 'Welcome bonus',
            status: 'confirmed',
            createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          },
          {
            id: 'tx-2',
            type: 'earn',
            amount: 100,
            description: 'Order loyalty reward - Rainbow Sails',
            businessId: 'rs-1',
            businessName: 'Rainbow Sails',
            status: 'confirmed',
            createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          },
        ],
      },
      settings: {
        notifications: {
          orders: true,
          messages: true,
          promotions: true,
          priceAlerts: true,
        },
        privacy: {
          showProfile: true,
          allowMessages: true,
          showActivity: false,
        },
        display: {
          theme: 'system',
          language: 'en',
          currency: 'AUD',
        },
      },
    };

    setStoredToken('demo-token');
    localStorage.setItem('mangoPersonalUser', JSON.stringify(demoUser));
    setUser(demoUser);
    return true;
  };

  const register = async (data: RegisterData): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register/personal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          password: data.password,
          phone: data.phone,
        }),
      });
      if (response.ok) {
        const res = await response.json();
        const token = res.token;
        const u = res.account ?? res.user;
        if (token && u) {
          const mapped = mapBackendToMangoUser(u);
          setStoredToken(token);
          localStorage.setItem('mangoPersonalUser', JSON.stringify(mapped));
          setUser(mapped);
          return true;
        }
      }
    } catch {
      if (isProduction()) return false;
      console.log('API not available, using demo registration');
    }

    if (isProduction()) return false; // No demo fallback on go-live

    // Demo mode (localhost only): create a new user
    const newUser: MangoUser = {
      id: `user-${Date.now()}`,
      mangoId: generateMangoId(),
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      subscription: 'free',
      createdAt: new Date().toISOString(),
      wallet: {
        id: `wallet-${Date.now()}`,
        address: '0x' + Array(40).fill(0).map(() => Math.floor(Math.random() * 16).toString(16)).join(''),
        mangoCredits: 50.00, // Welcome bonus
        pendingCredits: 0,
        weight: 100, // Starting weight
        transactionHistory: [
          {
            id: `tx-${Date.now()}`,
            type: 'earn',
            amount: 50,
            description: 'Welcome to Mango! Here are your first credits.',
            status: 'confirmed',
            createdAt: new Date().toISOString(),
          },
        ],
      },
      settings: {
        notifications: {
          orders: true,
          messages: true,
          promotions: true,
          priceAlerts: true,
        },
        privacy: {
          showProfile: true,
          allowMessages: true,
          showActivity: false,
        },
        display: {
          theme: 'system',
          language: 'en',
          currency: 'AUD',
        },
      },
    };

    setStoredToken('demo-token');
    localStorage.setItem('mangoPersonalUser', JSON.stringify(newUser));
    setUser(newUser);
    return true;
  };

  const logout = () => {
    clearStoredToken();
    localStorage.removeItem('mangoPersonalUser');
    setUser(null);
    setStaffAccounts([]);
  };

  const updateUser = (updates: Partial<MangoUser>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('mangoPersonalUser', JSON.stringify(updatedUser));
    }
  };

  // Paid Mango Personal plans have no billing yet: the backend only stores tier 'free' | 'premium'
  // (PersonalAccount.tier) and there is no Stripe checkout or upgrade endpoint for personal
  // accounts. Rather than call a route that doesn't exist (or fake an upgrade locally), report
  // that the upgrade didn't happen; the Upgrade page shows paid plans as "coming soon".
  const upgradeSubscription = async (_tier: SubscriptionTier): Promise<boolean> => false;

  return (
    <AuthContext.Provider value={{
      user,
      staffAccounts,
      isAuthenticated: !!user,
      loading,
      login,
      register,
      logout,
      updateUser,
      upgradeSubscription,
      refreshStaffAccounts,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
